import { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from 'axios';
import { toBig } from '../utils/bigMath';

const COINGECKO_API = 'https://api.coingecko.com/api/v3';
const DEV_PROXY_ROOT = '/api/coingecko';
const SHOULD_USE_PROXY = process.env.REACT_APP_USE_CORS_PROXY === 'true';
const FORCE_FALLBACK = process.env.REACT_APP_FORCE_FALLBACK_PRICES === 'true';
const MIN_FETCH_INTERVAL_MS = 60 * 1000; // limit refresh cadence to once per minute

const FALLBACK_COIN_DATA = [
  {
    id: 'bitcoin',
    symbol: 'btc',
    name: 'Bitcoin',
    image: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
    current_price: 65000,
    price_change_percentage_24h: 0.8
  },
  {
    id: 'ethereum',
    symbol: 'eth',
    name: 'Ethereum',
    image: 'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    current_price: 3200,
    price_change_percentage_24h: 1.4
  },
  {
    id: 'tether',
    symbol: 'usdt',
    name: 'Tether',
    image: 'https://assets.coingecko.com/coins/images/325/large/Tether-logo.png',
    current_price: 1,
    price_change_percentage_24h: 0
  },
  {
    id: 'usd-coin',
    symbol: 'usdc',
    name: 'USD Coin',
    image: 'https://assets.coingecko.com/coins/images/6319/large/USD_Coin_icon.png',
    current_price: 1,
    price_change_percentage_24h: 0
  },
  {
    id: 'ripple',
    symbol: 'xrp',
    name: 'XRP',
    image: 'https://assets.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png',
    current_price: 0.52,
    price_change_percentage_24h: -0.6
  },
  {
    id: 'solana',
    symbol: 'sol',
    name: 'Solana',
    image: 'https://assets.coingecko.com/coins/images/4128/large/solana.png',
    current_price: 150,
    price_change_percentage_24h: 2.1
  }
];

const PriceContext = createContext();

export const PriceProvider = ({ children }) => {
  const [prices, setPrices] = useState({});
  const [priceChanges, setPriceChanges] = useState({});
  const [allCoins, setAllCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState(0);
  const lastFetchAttemptRef = useRef(0);

  const applyCoinDataset = useCallback((coinArray, { isFallback = false, message } = {}) => {
    if (!Array.isArray(coinArray) || coinArray.length === 0) {
      return;
    }

    const pricesMap = {};
    const changesMap = {};

    coinArray.forEach((coin) => {
      if (!coin || !coin.symbol) return;
      const symbol = coin.symbol.toUpperCase();
      const priceValue = coin.current_price ?? coin.price ?? coin.priceUsd ?? 0;
      const changeValue = coin.price_change_percentage_24h ?? coin.change24h ?? 0;

      pricesMap[symbol] = toBig(priceValue).toString();
      changesMap[symbol] = toBig(changeValue).toString();
    });

    if (Object.keys(pricesMap).length === 0) {
      return;
    }

    setPrices(pricesMap);
    setPriceChanges(changesMap);
    setAllCoins(coinArray);
    if (isFallback) {
      setError(message ?? 'Live market data unavailable. Displaying fallback prices.');
    } else {
      setError(null);
    }
  }, []);

  const fetchPrices = useCallback(async () => {
    const now = Date.now();

    // If we've recently been rate-limited or hit a network/CORS issue, back off
    if (now < rateLimitedUntil) {
      return;
    }

    // Throttle refresh cadence to avoid spamming API
    if (now - lastFetchAttemptRef.current < MIN_FETCH_INTERVAL_MS) {
      return;
    }
    lastFetchAttemptRef.current = now;

    // Allow teams to opt into fallback data explicitly via env flag when live pricing is unavailable
    // or when live fetching is disabled in this environment (default for local development).
    if (FORCE_FALLBACK) {
      setLoading(false);
      setError(null);
      setLastUpdated(now);
      applyCoinDataset(FALLBACK_COIN_DATA, { isFallback: true });
      return;
    }

    const recentlyUpdated = lastUpdated !== null && now - lastUpdated < MIN_FETCH_INTERVAL_MS;

    if ((loading && lastUpdated !== null) || recentlyUpdated) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const mustUseProxy = process.env.NODE_ENV === 'development' && SHOULD_USE_PROXY;
      const baseUrl = mustUseProxy ? DEV_PROXY_ROOT : `${COINGECKO_API}/coins/markets`;
      const endpoint = mustUseProxy ? `${baseUrl}/coins/markets` : baseUrl;

      const params = {
        vs_currency: 'usd',
        order: 'market_cap_desc',
        per_page: 100,  // Increased from 20 to 100
        page: 1,
        sparkline: false,
        price_change_percentage: '24h'
      };

      const tryFetch = async (url) => axios.get(url, { params });

      try {
        const response = await tryFetch(endpoint);
        if (Array.isArray(response.data) && response.data.length > 0) {
          applyCoinDataset(response.data);
          setLastUpdated(Date.now());
          return;
        }
      } catch (directError) {
        // If we're being rate-limited or blocked, set a cooldown to avoid hammering the API
        if (directError?.response?.status === 429) {
          console.warn('CoinGecko rate limit hit; backing off for 15 minutes.');
          setRateLimitedUntil(now + 15 * 60 * 1000);
        } else if (directError?.code === 'ERR_NETWORK' || !directError?.response) {
          console.warn('Network/CORS issue when fetching prices; backing off for 10 minutes.');
          setRateLimitedUntil(now + 10 * 60 * 1000);
        }

        console.error('Error fetching prices (direct):', directError);
        throw directError;
      }
    } catch (err) {
      console.error('Error fetching prices:', err);

      setError((err && err.message) ? `Failed to fetch live prices: ${err.message}` : 'Failed to fetch live prices. Using fallback data.');

      applyCoinDataset(FALLBACK_COIN_DATA, {
        isFallback: true,
        message: 'Live prices unavailable. Displaying cached market snapshot.'
      });
    } finally {
      setLoading(false);
    }
  }, [applyCoinDataset, lastUpdated, loading, rateLimitedUntil]);

  // Format a price with proper decimal places
  const formatPrice = (coin, price) => {
    if (price === undefined || price === null) return '0.00';
    
    // Different formatting based on coin and price range
    if (['BTC', 'ETH', 'SOL', 'USDT', 'USDC'].includes(coin)) {
      return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else if (coin === 'XRP') {
      return price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    } else {
      // Default formatting based on price range
      if (price < 0.01) return price.toFixed(6);
      if (price < 1) return price.toFixed(4);
      if (price < 100) return price.toFixed(2);
      if (price < 10000) return price.toFixed(2);
      return price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
  };

  // Get the stored percentage change for a coin
  const getPercentChange = (coin) => {
    return priceChanges[coin] ?? '0';
  };

  // Initial fetch
  useEffect(() => {
    fetchPrices();
    const intervalId = setInterval(fetchPrices, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(intervalId);
  }, [fetchPrices]);

  return (
    <PriceContext.Provider 
      value={{ 
        prices, 
        priceChanges,
        allCoins,
        loading, 
        error, 
        lastUpdated,
        formatPrice,
        getPercentChange,
        refresh: fetchPrices
      }}
    >
      {children}
    </PriceContext.Provider>
  );
};

export const usePrices = () => {
  const context = useContext(PriceContext);
  if (!context) {
    throw new Error('usePrices must be used within a PriceProvider');
  }
  return context;
};

export default PriceContext;
